import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { initials } from '@/lib/format';
import { Plus, Copy, Check } from 'lucide-react';

export default function TeamTab() {
    const { profile } = useAuth();
    const [team, setTeam] = useState([]);
    const [showInvite, setShowInvite] = useState(false);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [role, setRole] = useState('agent');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [creating, setCreating] = useState(false);
    const [created, setCreated] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = async () => {
        const { data } = await supabase.from('profiles').select('*').eq('organization_id', profile.organization_id).order('role', { ascending: true });
        setTeam(data || []);
    };
    useEffect(() => { if (profile?.organization_id) load(); }, [profile?.organization_id]);

    const resetForm = () => { setEmail(''); setName(''); setRole('agent'); setPassword(''); setPhone(''); };

    const onInvite = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const r = await api.createTeamMember({ email, full_name: name, role, password, phone, org_id: profile.organization_id });
            setCreated(r);
            resetForm();
            await load();
            toast.success('Team member added');
        } catch (err) {
            console.log('Create account error:', err);
            toast.error(err.message || JSON.stringify(err) || 'Failed to create account');
        } finally { setCreating(false); }
    };

    const updateRole = async (id, newRole) => {
        await supabase.from('profiles').update({ role: newRole }).eq('id', id);
        toast.success('Role updated');
        load();
    };

    const deleteMember = async () => {
        try {
            await api.deleteTeamMember(deleteTarget.id);
            toast.success('Team member deleted');
            setTeam(prev => prev.filter(m => m.id !== deleteTarget.id));
            setDeleteTarget(null);
        } catch (err) {
            toast.error(err.message || 'Failed to delete member');
        }
    };

    const toggleActive = async (m) => {
        const next = !(m.is_active === false ? false : true);
        const action = next ? 'reactivate' : 'deactivate';
        if (!window.confirm(`Are you sure you want to ${action} ${m.full_name || m.email}?`)) return;
        const { error } = await supabase.from('profiles').update({ is_active: next }).eq('id', m.id);
        if (error) return toast.error(error.message);
        toast.success(next ? 'User reactivated' : 'User deactivated');
        load();
    };

    return (
        <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold text-aks-text">Team members <span className="text-[12px] text-gray-500 font-normal">({team.length})</span></h2>
                    <button onClick={() => setShowInvite(true)} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px]" data-testid="invite-agent-btn">
                        <Plus className="h-4 w-4" /> Add member
                    </button>
                </div>
                <div className="divide-y divide-gray-100">
                    {team.map((m) => {
                        const inactive = m.is_active === false;
                        return (
                            <div key={m.id} className={`flex items-center gap-3 py-3 ${inactive ? 'opacity-60' : ''}`} data-testid={`team-member-${m.id}`}>
                                <div className="h-9 w-9 rounded-full bg-aks-navy text-white flex items-center justify-center text-[11px] font-semibold">{initials(m.full_name || m.email)}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-[14px] text-aks-text flex items-center gap-2">
                                        {m.full_name || m.email}
                                        {inactive && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-bold uppercase">Deactivated</span>}
                                    </div>
                                    <div className="text-[12px] text-gray-500 truncate">{m.email}</div>
                                    {m.phone && <div className="text-[11px] text-gray-400">{m.phone}</div>}
                                </div>
                                <select value={m.role} onChange={(e) => updateRole(m.id, e.target.value)} disabled={m.id === profile.id || inactive} className="h-8 px-2 rounded border border-gray-300 text-[12px]">
                                    <option value="admin">Admin</option>
                                    <option value="agent">Agent</option>
                                </select>
                                {profile?.role === 'admin' && m.id !== profile.id && (
                                    <>
                                        <button onClick={() => toggleActive(m)} className={`h-8 px-2 rounded border text-[11px] font-semibold ${inactive ? 'border-aks-gold text-aks-navy hover:bg-aks-gold-soft' : 'border-gray-300 text-gray-600 hover:border-red-400 hover:text-red-600'}`} data-testid={`toggle-active-${m.id}`}>
                                            {inactive ? 'Reactivate' : 'Deactivate'}
                                        </button>
                                        <button onClick={() => setDeleteTarget(m)} className="h-8 px-2 rounded border border-red-300 text-red-600 hover:bg-red-50 text-[11px] font-semibold" data-testid={`delete-member-${m.id}`}>
                                            Delete
                                        </button>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {deleteTarget && (
                <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setDeleteTarget(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                        <h3 className="text-lg font-bold text-aks-text">Delete team member</h3>
                        <p className="text-[13px] text-gray-600">Are you sure you want to delete <span className="font-semibold">{deleteTarget.full_name || deleteTarget.email}</span>? This cannot be undone.</p>
                        <div className="flex gap-2">
                            <button onClick={() => setDeleteTarget(null)} className="flex-1 h-10 rounded-md border border-gray-300 text-[13px] font-medium">Cancel</button>
                            <button onClick={deleteMember} className="flex-1 h-10 rounded-md bg-red-600 text-white text-[13px] font-semibold hover:bg-red-700">Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {showInvite && (
                <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => { setShowInvite(false); setCreated(null); resetForm(); }}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                        {!created ? (
                            <form onSubmit={onInvite} className="space-y-4" data-testid="invite-form">
                                <h3 className="text-lg font-bold text-aks-text">Add team member</h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <label className="text-[12px] font-medium">Email *</label>
                                        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" />
                                    </div>
                                    <div>
                                        <label className="text-[12px] font-medium">Full name</label>
                                        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" />
                                    </div>
                                    <div>
                                        <label className="text-[12px] font-medium">Phone</label>
                                        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" placeholder="+971…" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-[12px] font-medium">Password *</label>
                                        <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" placeholder="Min. 8 characters" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-[12px] font-medium">Role</label>
                                        <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]">
                                            <option value="agent">Agent</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => { setShowInvite(false); resetForm(); }} className="flex-1 h-10 rounded-md border border-gray-300 text-[13px] font-medium">Cancel</button>
                                    <button type="submit" disabled={creating} className="flex-1 h-10 rounded-md bg-aks-navy text-white text-[13px] font-semibold disabled:opacity-60">{creating ? 'Creating…' : 'Create account'}</button>
                                </div>
                            </form>
                        ) : (
                            <div className="space-y-3">
                                <h3 className="text-lg font-bold text-aks-text flex items-center gap-2"><Check className="h-5 w-5 text-aks-success" /> Member created</h3>
                                <p className="text-[13px] text-gray-600">Share these credentials with your teammate:</p>
                                <CopyField label="Email" value={created.email} />
                                <CopyField label="Password" value={created.temp_password} mono />
                                <button onClick={() => { setShowInvite(false); setCreated(null); }} className="w-full h-10 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px]">Done</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function CopyField({ label, value, mono }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); };
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</div>
            <div className="flex items-center gap-2">
                <code className={`flex-1 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-[12px] truncate ${mono ? 'font-mono' : ''}`}>{value}</code>
                <button onClick={copy} className="h-9 px-3 rounded-md border border-gray-300 hover:border-aks-gold text-[12px] inline-flex items-center gap-1.5">
                    {copied ? <><Check className="h-4 w-4 text-aks-success" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
                </button>
            </div>
        </div>
    );
}
